/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import Subscription from '../../../domain/models/Subscription';
import AI from '../../../domain/models/AI';
import { DocumentData, OrderByDirection, QueryDocumentSnapshot } from 'firebase/firestore';
import getSubscriptions from '../../../domain/use_cases/subscriptions/getSubscriptions';
import getSubscriptionById from '../../../domain/use_cases/subscriptions/getSubscriptionById';
import getSubscriptionsOrderedBy from '../../../domain/use_cases/subscriptions/getSubscriptionsOrderedBy';
import searchSubscriptions from '../../../domain/use_cases/subscriptions/searchSubscriptions';
import { ISubscriptionView } from '../../../domain/interfaces/ISubscriptionView';
import addUserToSubscription, { AddUserToSubscriptionStatusFailure } from '../../../domain/use_cases/subscriptions/addUserToSubscription';
import removeUserFromSubscription from '../../../domain/use_cases/subscriptions/removeUserFromSubscription';
import getSubscriptionUsers from '../../../domain/use_cases/subscriptions/getSubscriptionUsers';
import UserDoc from '../../../domain/models/UserDoc';
import AuthenticationContext from '../auth/AuthenticationContext';
import getUserById from '../../../domain/use_cases/users/getUserById';
import getSubscriptionsBySender from '../../../domain/use_cases/subscriptions/getSubscriptionsBySender';
import getSubscriptionsWithFilters, { SubscriptionFilters } from '../../../domain/use_cases/subscriptions/getSubscriptionsWithFilters';
import { Dayjs } from 'dayjs';
import { ICreditMap } from '@leedpulse-monorepo/interfaces';

type SubscriptionStats = {
  contactsCount: number;
  smsCount: number;
  jobsCount: number;
  totalCostSms: number;
  notificationsPendingCount: number;
  notficationsCount: number;
  replyRate: number;
  engagedCount: number;
  credits: ICreditMap;
};

interface QualificationDistribution {
  interested: number;
  considering: number;
  not_interested: number;
  not_responsive: number;
  not_available: number;
}

const SubscriptionsContext = createContext({
  count: 0,
  activeCount: 0,
  expiredCount: 0,
  items: [] as Subscription[],
  selected: null as Subscription | null,
  selectionStats: null as SubscriptionStats | null,
  subscriptionUsers: [] as UserDoc[],
  lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
  searchResults: [] as ISubscriptionView[],
  searchValue: null as string | null,
  noSearchResults: false,
  favoritesOnly: false,
  errorMessage: '',
  ais: new Map<string, AI>(),
  addUser: (email: string) => { },
  removeUser: (email: string) => { },
  fetch: (fetchFavoritesOnly?: boolean, limit?: number) => { },
  fetchFavorites: () => { },
  fetchUsers: (_: string) => { },
  fetchAllUsers: (_: Record<string, { roles: string[] }>) => { },
  fetchNext: (limit?: number) => { },
  fetchAndSelectById: (_: string, scenarioId?: string, userUid?: string, period?: [Dayjs | null, Dayjs | null]) => { },
  fetchSubscriptionsOnly: (orderBy: string, sort: OrderByDirection, limit?: number) => { },
  fetchBySender: (_: string) => { },
  fetchWithFilters: (filters: SubscriptionFilters, orderBy: string | undefined, sort: OrderByDirection | undefined, limit?: number) => { },
  fetchNextWithFilters: (filters: SubscriptionFilters, orderBy: string | undefined, sort: OrderByDirection | undefined, limit?: number) => { },
  select: (_: Subscription) => { },
  search: async (_: string, _page?: number, _hitsPerPage?: number, _filters?: Record<string, unknown>) => { },
  resetSearch: () => { },
  reset: () => { },
  status: ContextStatus.IDLE,
  statusAddRemoveUser: ContextStatus.IDLE,
  statusCount: ContextStatus.IDLE,
  selectionStatus: ContextStatus.IDLE,
  qualificationDistribution: {
    interested: 0,
    considering: 0,
    not_interested: 0,
    not_responsive: 0,
    not_available: 0
  } as QualificationDistribution,
});

export const SUBSCRIPTIONS_FETCH_LIMIT = 10;

export const SubscriptionsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [count, setCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [expiredCount, setExpiredCount] = useState(0);
  const [items, setItems] = useState([] as Subscription[]);
  const [selected, setSelected] = useState(null as Subscription | null);
  const [selectionStats, setSelectionStats] = useState(
    null as SubscriptionStats | null
  );
  const [lastVisible, setLastVisible] = useState(
    {} as QueryDocumentSnapshot<DocumentData, DocumentData>
  );
  const [subscriptionUsers, setSubscriptionUsers] = useState([] as UserDoc[]);
  const [searchResults, setSearchResults] = useState<ISubscriptionView[]>([]);
  const [noSearchResults, setNoSearchResults] = useState(false);
  const [searchValue, setSearchValue] = useState(null as string | null);
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [selectionStatus, setSelectionStatus] = useState(ContextStatus.IDLE);
  const [statusCount, setStatusCount] = useState(ContextStatus.IDLE);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusAddRemoveUser, setStatusAddRemoveUser] = useState(
    ContextStatus.IDLE
  );
  const [qualificationDistribution, setQualificationDistribution] =
    useState<QualificationDistribution>({
      interested: 0,
      considering: 0,
      not_interested: 0,
      not_responsive: 0,
      not_available: 0
    });
  const [ais, setAis] = useState(new Map<string, AI>());

  const authContext = useContext(AuthenticationContext);

  const fetchFavorites = async () => {
    if (!authContext.authentication?.id) return;

    const userResponse = await getUserById(
      authContext.authentication?.id || ''
    );
    const useCaseResponse = await getSubscriptions(
      1000,
      undefined,
      Object.keys(userResponse?.idSubscription || {})
    );
    setItems(useCaseResponse.subscriptions);
    setAis(useCaseResponse.ais);
  };

  const fetchUsers = async (subscriptionId: string) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getSubscriptionUsers(subscriptionId);
      setSubscriptionUsers(useCaseResponse.users);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchAllUsers = async (subscriptionIds: Record<string, { roles: string[] }>) => {
    setStatus(ContextStatus.LOADING);
    try {
      const allUsers: UserDoc[] = [];
      for (const subscriptionId of Object.keys(subscriptionIds)) {
        const useCaseResponse = await getSubscriptionUsers(subscriptionId);
        allUsers.push(...useCaseResponse.users);
      }
      const usersFiltered = Array.from(new Set(allUsers.map(user => user.id)))
        .map(id => {
          return allUsers.find(user => user.id === id)
        })
        .filter((user): user is UserDoc => user !== undefined);
      setSubscriptionUsers(usersFiltered);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const addUser = async (email: string) => {
    setStatusAddRemoveUser(ContextStatus.LOADING);
    try {
      if (!selected || !authContext.authentication) {
        setStatusAddRemoveUser(ContextStatus.ERROR);
        return;
      }
      await addUserToSubscription(selected, authContext.authentication, email);
      setStatusAddRemoveUser(ContextStatus.SUCCESS);
    } catch (e) {
      if (e instanceof AddUserToSubscriptionStatusFailure) {
        setErrorMessage(e.message);
      }
      setStatusAddRemoveUser(ContextStatus.ERROR);
    }
  };

  const removeUser = async (email: string) => {
    setStatusAddRemoveUser(ContextStatus.LOADING);
    try {
      if (!selected || !authContext.authentication) {
        setStatusAddRemoveUser(ContextStatus.ERROR);
        return;
      }
      await removeUserFromSubscription(
        selected,
        authContext.authentication,
        email
      );
      setStatusAddRemoveUser(ContextStatus.SUCCESS);
    } catch (e) {
      console.log('error here', e);
      setStatusAddRemoveUser(ContextStatus.ERROR);
    }
  };

  const fetch = async (
    fetchFavoritesOnly = false,
    limit = SUBSCRIPTIONS_FETCH_LIMIT
  ) => {
    setStatus(ContextStatus.LOADING);
    setFavoritesOnly(fetchFavoritesOnly);
    try {
      const useCaseResponse = await getSubscriptions(
        limit,
        undefined,
        undefined
      );
      setItems(useCaseResponse.subscriptions);
      setLastVisible(useCaseResponse.lastVisible);
      setCount(useCaseResponse.totalCollectionCount);
      setAis(useCaseResponse.ais);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchSubscriptionsOnly = async (
    orderBy: string,
    sort: OrderByDirection,
    limit = SUBSCRIPTIONS_FETCH_LIMIT
  ) => {
    try {
      const useCaseResponse = await getSubscriptionsOrderedBy(
        orderBy,
        sort,
        limit,
      );
      setItems(useCaseResponse.subscriptions);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  }

  const fetchNext = async (limit = SUBSCRIPTIONS_FETCH_LIMIT) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getSubscriptions(
        limit,
        lastVisible,
        undefined
      );
      setItems([...items, ...useCaseResponse.subscriptions]);
      setLastVisible(useCaseResponse.lastVisible);
      // Merge new AIs with existing ones
      const mergedAis = new Map(ais);
      useCaseResponse.ais.forEach((ai, subscriptionId) => {
        mergedAis.set(subscriptionId, ai);
      });
      setAis(mergedAis);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchNextWithFilters = async (filters: SubscriptionFilters, orderBy: string | undefined, sort: OrderByDirection | undefined, limit = SUBSCRIPTIONS_FETCH_LIMIT) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getSubscriptionsWithFilters(
        filters,
        orderBy,
        sort,
        limit,
        lastVisible
      );
      setItems([...items, ...useCaseResponse.subscriptions]);
      setLastVisible(useCaseResponse.lastVisible);
      // Merge new AIs with existing ones
      const mergedAis = new Map(ais);
      useCaseResponse.ais.forEach((ai, subscriptionId) => {
        mergedAis.set(subscriptionId, ai);
      });
      setAis(mergedAis);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchBySender = async (sender: string) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getSubscriptionsBySender(sender);
      setItems(useCaseResponse.subscriptions);
      setCount(useCaseResponse.totalCount);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchWithFilters = async (filters: SubscriptionFilters, orderBy: string | undefined, sort: OrderByDirection | undefined, limit = SUBSCRIPTIONS_FETCH_LIMIT) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getSubscriptionsWithFilters(filters,
        orderBy,
        sort,
        limit
      );
      setItems(useCaseResponse.subscriptions);
      setCount(useCaseResponse.totalCount);
      setLastVisible(useCaseResponse.lastVisible);
      setAis(useCaseResponse.ais);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const search = async (query: string, page = 0, hitsPerPage = 20, filters?: Record<string, unknown>) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await searchSubscriptions(authContext.authentication, query, page, hitsPerPage, filters);
      setSearchResults(useCaseResponse.hits);
      setNoSearchResults(useCaseResponse.hits.length === 0);
      setSearchValue(query);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const resetSearch = () => {
    setSearchResults([]);
    setNoSearchResults(false);
    setSearchValue(null);
  };

  const reset = () => {
    setItems([]);
    setCount(0);
    setSelected(null);
    setSubscriptionUsers([]);
    setLastVisible({} as QueryDocumentSnapshot<DocumentData, DocumentData>);
    setSearchResults([]);
    setNoSearchResults(false);
    setSearchValue(null);
    setErrorMessage('');
    setStatus(ContextStatus.IDLE);
    setStatusCount(ContextStatus.IDLE);
    setStatusAddRemoveUser(ContextStatus.IDLE);
    setAis(new Map<string, AI>());
  };

  const fetchAndSelectById = async (id: string, scenarioId?: string, userUid?: string, period?: [Dayjs | null, Dayjs | null]) => {
    setSelectionStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getSubscriptionById(id, scenarioId, userUid, period);
      if (useCaseResponse) {
        setSelected(useCaseResponse.subscription);
        setSelectionStats({
          contactsCount: useCaseResponse.contactCount,
          smsCount: useCaseResponse.smsCount,
          jobsCount: useCaseResponse.jobsCount,
          totalCostSms: useCaseResponse.totalCostSms,
          notificationsPendingCount: useCaseResponse.notificationsPendingCount,
          notficationsCount: useCaseResponse.notificationsCount,
          replyRate: useCaseResponse.replyRate ?? 0,
          engagedCount: useCaseResponse.engagedCount ?? 0,
          credits: useCaseResponse.subscription.credits ?? { sms: 0, minutes: 0, contacts: 0 }
        });
        setQualificationDistribution({
          interested: useCaseResponse.qualificationDistribution.interested,
          considering: useCaseResponse.qualificationDistribution.considering,
          not_interested:
            useCaseResponse.qualificationDistribution.not_interested,
          not_responsive: useCaseResponse.qualificationDistribution.not_responsive,
          not_available: useCaseResponse.qualificationDistribution.not_available
        });
        setSelectionStatus(ContextStatus.SUCCESS);
      } else {
        setSelectionStatus(ContextStatus.ERROR);
      }
    } catch (e) {
      setSelectionStatus(ContextStatus.ERROR);
    }
  };

  const select = (subscription: Subscription) => {
    setSelected(subscription);
    setSelectionStatus(ContextStatus.SUCCESS);
  };

  return (
    <SubscriptionsContext.Provider
      value={{
        count,
        activeCount,
        expiredCount,
        items,
        selected,
        selectionStats,
        subscriptionUsers,
        lastVisible,
        searchResults,
        noSearchResults,
        searchValue,
        favoritesOnly,
        errorMessage,
        addUser,
        removeUser,
        fetch,
        fetchFavorites,
        fetchUsers,
        fetchAllUsers,
        fetchNext,
        fetchAndSelectById,
        fetchSubscriptionsOnly,
        fetchBySender,
        fetchWithFilters,
        fetchNextWithFilters,
        select,
        search,
        resetSearch,
        reset,
        status,
        statusAddRemoveUser,
        statusCount,
        selectionStatus,
        qualificationDistribution,
        ais,
      }}
    >
      {children}
    </SubscriptionsContext.Provider>
  );
};

export default SubscriptionsContext;
